import React, { useState } from 'react';
import { OB_STEPS, PLATFORMS, GREEN, BLACK, GREEN_LIGHT } from '../constants';
import { card, cardGreen, mono, labelSt } from '../styles';
import { NavBar, Btn, ProgressBar } from '../components/UI';
import Section from '../components/Section';

export default function AgencyClientView({ client, onBack, onUpdate }) {
  const [tab,   setTab]   = useState('ob');
  const [saved, setSaved] = useState(false);

  const ob      = client.onboarding;
  const done    = ob.completedSteps || [];
  const plats   = client.platforms ? client.platforms.split(',').map(p => p.trim()).filter(Boolean) : [];
  const hFilled = client.sections.filter(s => s.items && s.items.length > 0).length;

  function save(updated) {
    onUpdate(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  }

  function updateSection(id, items) {
    save({ ...client, sections: client.sections.map(s => s.id === id ? { ...s, items } : s) });
  }

  const TABS = [['ob', 'Onboarding'], ['hw', 'Handover Guide'], ['sub', 'Submissions']];

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f2', fontFamily: 'system-ui, sans-serif' }}>
      <NavBar
        title={client.name}
        sub={`ID: ${client.clientId} — PIN: ${client.pin}`}
        onBack={onBack}
        right={saved && <span style={{ fontSize: 12, color: '#16a34a', fontFamily: 'monospace' }}>Saved</span>}
      />
      <div style={{ maxWidth: 1020, margin: '0 auto', padding: '22px 16px' }}>

        {/* Stats row */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
          <div style={card()}>
            <div style={labelSt}>Client</div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 6 }}>{client.name}</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {plats.map(p => <span key={p} style={{ background: GREEN_LIGHT, padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>{p}</span>)}
            </div>
          </div>
          <div style={cardGreen()}>
            <div style={{ ...labelSt, color: 'rgba(0,0,0,.4)' }}>Onboarding</div>
            <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}>
              {done.length}/{OB_STEPS.length}
            </div>
            <div style={{ background: 'rgba(0,0,0,.1)', borderRadius: 100, height: 4, margin: '8px 0 3px' }}>
              <div style={{ width: `${(done.length / OB_STEPS.length) * 100}%`, background: BLACK, borderRadius: 100, height: '100%' }} />
            </div>
            <div style={{ fontSize: 10, fontFamily: 'monospace', opacity: 0.5 }}>steps complete</div>
          </div>
          <div style={card()}>
            <div style={labelSt}>Handover</div>
            <div style={mono}>{hFilled}/{client.sections.length} sections</div>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 3, marginBottom: 12, background: '#efefed', borderRadius: 11, padding: 3 }}>
          {TABS.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={{
              flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', fontSize: 12, fontWeight: 700,
              background: tab === id ? '#fff' : 'transparent',
              color: tab === id ? BLACK : '#999',
              cursor: 'pointer', fontFamily: 'system-ui, sans-serif',
              boxShadow: tab === id ? '0 1px 3px rgba(0,0,0,.07)' : 'none',
            }}>{label}</button>
          ))}
        </div>

        {/* Onboarding tab */}
        {tab === 'ob' && (
          <div>
            {OB_STEPS.map(s => {
              const isDone = done.includes(s.id);
              return (
                <div key={s.id} style={card({ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 12 })}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, flexShrink: 0, background: isDone ? GREEN : '#f5f5f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700 }}>
                    {isDone ? 'OK' : '—'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>{s.label}</div>
                    {s.id === 'agreement'     && isDone && <div style={mono}>Signed by {ob.signedBy} on {new Date(ob.signedAt).toLocaleDateString()}</div>}
                    {s.id === 'questionnaire' && isDone && <div style={mono}>{Object.values(ob.answers || {}).filter(v => v && v.trim()).length} fields submitted</div>}
                    {s.id === 'logins'        && isDone && <div style={mono}>{Object.keys(ob.logins || {}).filter(k => Object.values(ob.logins[k] || {}).some(v => v && v.trim())).length} platforms submitted</div>}
                    {s.id === 'files'         && isDone && <div style={mono}>{(ob.files || []).length} files uploaded</div>}
                    {!isDone && <div style={mono}>Not completed yet</div>}
                  </div>
                  <span style={{ background: isDone ? GREEN : '#f0f0ec', color: isDone ? BLACK : '#555', padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>
                    {isDone ? 'Done' : 'Pending'}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Handover tab */}
        {tab === 'hw' && (
          <div>
            {client.sections.map(sec => (
              <Section key={sec.id} sec={sec} editable onUpdate={items => updateSection(sec.id, items)} />
            ))}
          </div>
        )}

        {/* Submissions tab */}
        {tab === 'sub' && (
          <div>
            {/* Questionnaire */}
            {Object.keys(ob.answers || {}).length > 0 && (
              <div style={card({ marginBottom: 10 })}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Questionnaire Responses</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
                  {Object.entries(ob.answers).filter(([, v]) => v && v.trim()).map(([k, v]) => (
                    <div key={k} style={{ background: '#f8f8f6', borderRadius: 9, padding: '9px 12px' }}>
                      <div style={labelSt}>{k}</div>
                      <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Logins */}
            {Object.keys(ob.logins || {}).length > 0 && (
              <div style={card({ marginBottom: 10 })}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Submitted Credentials</div>
                {Object.entries(ob.logins).filter(([, v]) => Object.values(v || {}).some(x => x && x.trim())).map(([pid, fields]) => {
                  const P = PLATFORMS.find(x => x.id === pid);
                  return (
                    <div key={pid} style={{ background: '#f8f8f6', borderRadius: 9, padding: '10px 12px', marginBottom: 6 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>{P ? P.label : pid}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 5 }}>
                        {Object.entries(fields).filter(([, v]) => v && v.trim()).map(([f, v]) => (
                          <div key={f}>
                            <div style={labelSt}>{f}</div>
                            <div style={{ fontSize: 12, fontFamily: 'monospace', background: '#fff', padding: '3px 7px', borderRadius: 5, border: '1px solid #e0e0d8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {f.toLowerCase().includes('password') ? '••••••••' : v}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Files */}
            {(ob.files || []).length > 0 && (
              <div style={card()}>
                <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Uploaded Files</div>
                {ob.files.map((f, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', background: '#f8f8f6', borderRadius: 8, marginBottom: 5 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{f.name}</div>
                      <div style={mono}>{new Date(f.uploadedAt).toLocaleDateString()}</div>
                    </div>
                    <span style={{ background: GREEN_LIGHT, color: BLACK, padding: '2px 7px', borderRadius: 20, fontSize: 11, fontWeight: 600 }}>Uploaded</span>
                  </div>
                ))}
              </div>
            )}

            {!Object.keys(ob.answers || {}).length && !Object.keys(ob.logins || {}).length && !(ob.files || []).length && (
              <div style={{ textAlign: 'center', padding: 50, ...mono }}>Nothing submitted yet</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
